import {
  Box,
  Button,
  Container,
  FormControlLabel,
  Grid,
  InputAdornment,
  Typography,
} from '@mui/material';
import {useEffect, useState, useRef, useCallback} from 'react';
import {ReactComponent as Blur} from '../../../assests/svg/login-bg-1.svg';
import {ReactComponent as Blur2} from '../../../assests/svg/login-bg-2.svg';
import {t} from 'i18next';
import {AurTextField} from '../../common/aur-text-field/AurTextField';
import AurBanner from '../../common/aur-banner/AurBanner';
import {login as loginService} from '../../../api/Authentication';
import {getIsMaintenanceEnabled} from '../../../api/UserManagementService';
import AurIcon, {IconColors, IconNames} from '../../common/aur-icon/AurIcon';
import {EMAIL_ADDRESS_REGEX, ERROR_STATUS_CODE} from '../../../constants/constants';
import AurCheckbox from '../../common/aur-checkbox/AurCheckbox';
import {LeftPaneBrand} from './shared';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {resetChangeTracker} from '../../../redux/slices/globalChangeTrackerSlice';
import {useDispatch, useSelector} from 'react-redux';
import {
  isAuthenticated,
  setUserEmail,
  selectEmail,
} from '../../../redux/slices/userSlice';
import {useLocation} from 'react-router-dom';
import {resetPageState} from '../../../redux/slices/pageViewStateSlice';
import {useAnalytics} from '../../../hooks/useAnalytics';
import navConstants from '../../../constants/navConstants';
import config from '../../../config';
import {
  GA_DATA_MODE_AUTO,
  GA_ERROR_CODES,
  GA_ERROR_TYPES,
  GA_EVENT_NAMES,
} from '../../../constants/analyticsConstants';

const isUnavailableStatus = (status?: number) =>
  status === ERROR_STATUS_CODE.STATUS_502 ||
  status === ERROR_STATUS_CODE.STATUS_503;

/*
Component for login page
*/
export const Login = () => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState<string>('');
  const [isValid, setValid] = useState<boolean>(false);
  const [animationValidCount, setAnimationValidCount] = useState<number>(0);

  const [errorText, setErrorText] = useState<string>('');
  const [errorTextEmail, setErrorTextEmail] = useState<string>('');
  const [errorStatus, setErrorStatus] = useState<boolean>(false);
  const [errorType, setErrorType] = useState<
    'error' | 'warning' | 'success' | 'info'
  >('error');
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [rememberMeChecked, setRememberMeChecked] = useState<boolean>(false);
  const [isInputFieldDisabled, setIsInputFieldDisabled] =
    useState<boolean>(false);
  const warningErrorTypes = ['email_locked', 'email_inactive'];
  const isUserAuthenticated = useSelector(isAuthenticated);
  const submitLock = useRef(false);

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const invitedEmailIdFromQuery = queryParams.get('emailId');
  const redirectData = queryParams.get('redirectData');

  const navigate = useNavigate();
  const {trackGAEvent} = useAnalytics();
  const savedEmail = useSelector(selectEmail);
  const [maintenanceGate, setMaintenanceGate] = useState<
    'pending' | 'open' | 'blocked'
  >('pending');

  const redirectToUnavailable = useCallback(() => {
    setMaintenanceGate('blocked');
    navigate(navConstants.auth.unavailable, {replace: true});
  }, [navigate]);

  useEffect(() => {
    let cancelled = false;
    const gateLogin = async () => {
      const inMaintenanceMode = await getIsMaintenanceEnabled();
      if (cancelled) {
        return;
      }
      if (inMaintenanceMode) {
        redirectToUnavailable();
        return;
      }
      setMaintenanceGate('open');
    };
    void gateLogin();
    const interval = window.setInterval(
      gateLogin,
      config.MAINTENANCE_STATUS_POLL_MS
    );
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [navigate, redirectToUnavailable]);

  useEffect(() => {
    if (savedEmail) {
      setEmail(savedEmail);
      setRememberMeChecked(true);
      setValid(true);
    }
    const errorQueryParamValue = searchParams.get('error');
    if (errorQueryParamValue) {
      const errorMessage = t(`auth.login.${errorQueryParamValue}`);

      if (warningErrorTypes.includes(errorQueryParamValue)) {
        setErrorType('warning');
      }

      const errorMap: Record<string, string> = {
        email_locked: t('auth.login.email_locked'),
        email_inactive: t('auth.login.email_inactive'),
      };

      const resolvedError = errorMap[errorMessage] || errorMessage;

      setErrorText(resolvedError);
      trackGAEvent({
        eventName: GA_EVENT_NAMES.ERROR,
        page: GA_DATA_MODE_AUTO,
        error: {
          type: GA_ERROR_TYPES.AUTHENTICATION_ERROR,
          text: resolvedError,
          code: GA_ERROR_CODES.NOT_AUTHORIZED_401,
        },
      });
      setErrorStatus(true);
    }
  }, [dispatch, searchParams]);

  useEffect(() => {
    if (isUserAuthenticated) {
      navigate('/welcome-multi-client');
    }
  }, [isUserAuthenticated, navigate]);

  useEffect(() => {
    // clear page view state
    dispatch(resetPageState());
    // clear change tracker
    dispatch(resetChangeTracker());
  }, []);

  useEffect(() => {
    const newEmail = redirectData && JSON.parse(atob(redirectData)).email;
    if (invitedEmailIdFromQuery) {
      setEmail(invitedEmailIdFromQuery);
      setIsInputFieldDisabled(true);
      setValid(true);
    }
    if (newEmail && maintenanceGate === 'open') {
      setEmail(newEmail);
      handleLogin(newEmail);
      setIsInputFieldDisabled(true);
    }
  }, [searchParams, maintenanceGate]);

  const handleRememberMeChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const checked = event.target.checked;
    setRememberMeChecked(checked);

    if (checked) {
      dispatch(setUserEmail(email));
    } else {
      dispatch(setUserEmail(null));
    }
  };
  /*
  Function for handling email textfield change 
  */
  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setEmail(value);

    // Validate email only if at least 3 characters are entered
    if (value.length >= 3) {
      const emailRegex = EMAIL_ADDRESS_REGEX;
      if (emailRegex.test(value)) {
        setValid(true);
        setAnimationValidCount(1);
        setErrorTextEmail('');
        setErrorStatus(false);
        setErrorType('error');
      } else {
        setValid(false);
        setErrorTextEmail(t('auth.login.emailInvalid'));
      }
    } else {
      setValid(false);
      setErrorTextEmail('');
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (submitLock.current) return;
    submitLock.current = true;

    setSubmitting(true);

    if (!isValid) {
      setSubmitting(false);
      submitLock.current = false;
      return;
    }

    handleLogin(email);
  };

  const handleLogin = async (email: string) => {
    try {
      const inMaintenanceMode = await getIsMaintenanceEnabled();
      if (inMaintenanceMode) {
        redirectToUnavailable();
        setSubmitting(false);
        submitLock.current = false;
        return;
      }

      await loginService(email, redirectData);
    } catch (error: any) {
      if (isUnavailableStatus(error?.response?.status)) {
        redirectToUnavailable();
        return;
      }
      setErrorStatus(true);
      setErrorText(t('auth.login.sso_init_error'));
      setSubmitting(false);
      submitLock.current = false;
    }
  };

  if (maintenanceGate !== 'open') {
    return null;
  }

  return (
    <Box className="login-main-container" role="main">
      <Container maxWidth={false} className="login-page">
        <Blur
          className={`login-page_logo ${isValid ? 'valid' : animationValidCount > 0 ? 'invalid' : ''}`}
          aria-hidden
        />
        <Blur2
          className={`login-page_logo2 ${isValid ? 'valid' : animationValidCount > 0 ? 'invalid' : ''}`}
          aria-hidden
        />
        <Grid container>
          <LeftPaneBrand />
          <Grid item xs={6} md={6}>
            <form onSubmit={handleSubmit}>
              <Box className="login-form">
                <div className="login-content">
                  <div className="sso-pill typo-text buttonLarge_size">
                    <img
                      className="key-img"
                      src="/assests/images/Vector_key.svg"
                      alt="icon"
                    />
                    <span>{t('auth.login.sso')}</span>
                  </div>

                  <Typography
                    className="typo-text headline2_size mist-color"
                    component="h1"
                  >
                    {t('auth.login.loginMsg1')}
                  </Typography>
                  <Typography className="typo-text body2_size mist-color-900">
                    {t('auth.login.loginMsg2')}
                  </Typography>
                </div>

                <div className="login-fields">
                  {errorStatus && (
                    <AurBanner errorText={errorText} errorType={errorType} />
                  )}
                  <AurTextField
                    ariaLabel={t('auth.login.emailHelpText1')}
                    helperText={
                      isValid
                        ? ''
                        : errorTextEmail || t('auth.login.emailHelpText')
                    }
                    sx={{
                      '& input:-webkit-autofill': {
                        WebkitBoxShadow: (theme) =>
                          `0 0 0 100px ${theme.palette.background.paper} inset !important`,
                        WebkitTextFillColor: (theme) =>
                          theme.palette.text.primary,
                        backgroundColor: (theme) =>
                          theme.palette.background.default,
                        color: (theme) => theme.palette.text.primary,
                        borderRadius: '8px !important',
                      },
                    }}
                    label={t('auth.login.emailLabel')}
                    value={email}
                    onChange={handleEmailChange}
                    variant="filled"
                    className="aur-textfield custom-text-field email-text-field"
                    error={Boolean(errorTextEmail)}
                    InputProps={{
                      disableUnderline: true,
                      endAdornment: errorTextEmail ? (
                        <InputAdornment position="end">
                          <AurIcon
                            icon={IconNames.Error}
                            color={IconColors.Critical}
                          />
                        </InputAdornment>
                      ) : null,

                      className: errorTextEmail && 'inputError',
                    }}
                    disabled={isInputFieldDisabled}
                  />
                  <FormControlLabel
                    control={
                      <AurCheckbox
                        checked={rememberMeChecked}
                        onChange={handleRememberMeChange}
                      />
                    }
                    label={t('auth.login.remembarEmailLabel')}
                    className="custom-checkbox"
                  />
                </div>

                <Button
                  variant="contained"
                  className="typo-text buttonLarge_size custom-button button-logo"
                  sx={{
                    textTransform: 'none',
                    WebkitTapHighlightColor: 'transparent',
                    '&.MuiButton-root:focus': {
                      border: 'none !important',
                    },
                    '&.MuiButton-root:focus-visible': {
                      border: 'none !important',
                    },
                  }}
                  type="submit"
                  disabled={submitting || !isValid}
                >
                  {t('buttons.continue')}
                </Button>
              </Box>
            </form>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};
